import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * User-level dialogue preferences, persisted client-side in localStorage
 * (matching useResizableSidebar's `pepper.*` key convention). These are
 * per-browser settings that apply across every matter and dialogue.
 *
 * Settings:
 *  - matterSelectionBehavior: what happens when the user opens a matter.
 *    'new-chat' (the default — matches the original behavior) always lands on
 *    the empty new-chat state; 'most-recent' redirects to the matter's latest
 *    dialogue. Consumed by MatterPage's open-time auto-redirect.
 *  - referencesPillBehavior: what happens when the user clicks the references
 *    pill on an agent response. 'references-table' (the default — matches the
 *    original behavior) opens the references table; 'record-viewer' also
 *    auto-selects the first reference so the record (raw) view opens straight
 *    away. Consumed by ReferencesPanel.
 */

export type MatterSelectionBehavior = 'most-recent' | 'new-chat';
export type ReferencesPillBehavior = 'references-table' | 'record-viewer';

const MATTER_SELECTION_KEY = 'pepper.dialoguePrefs.matterSelectionBehavior';
const REFERENCES_PILL_KEY = 'pepper.dialoguePrefs.referencesPillBehavior';

const DEFAULT_MATTER_SELECTION: MatterSelectionBehavior = 'new-chat';
const DEFAULT_REFERENCES_PILL: ReferencesPillBehavior = 'references-table';

function readMatterSelection(): MatterSelectionBehavior {
    try {
        const raw = localStorage.getItem(MATTER_SELECTION_KEY);
        return raw === 'most-recent' || raw === 'new-chat' ? raw : DEFAULT_MATTER_SELECTION;
    } catch {
        return DEFAULT_MATTER_SELECTION;
    }
}

function readReferencesPill(): ReferencesPillBehavior {
    try {
        const raw = localStorage.getItem(REFERENCES_PILL_KEY);
        return raw === 'references-table' || raw === 'record-viewer'
            ? raw
            : DEFAULT_REFERENCES_PILL;
    } catch {
        return DEFAULT_REFERENCES_PILL;
    }
}

export const useDialoguePreferencesStore = defineStore('dialoguePreferences', () => {
    const matterSelectionBehavior = ref<MatterSelectionBehavior>(readMatterSelection());
    const referencesPillBehavior = ref<ReferencesPillBehavior>(readReferencesPill());

    function setMatterSelectionBehavior(behavior: MatterSelectionBehavior) {
        matterSelectionBehavior.value = behavior;
        try {
            localStorage.setItem(MATTER_SELECTION_KEY, behavior);
        } catch {
            /* best-effort — the in-memory value still drives behavior this session */
        }
    }

    function setReferencesPillBehavior(behavior: ReferencesPillBehavior) {
        referencesPillBehavior.value = behavior;
        try {
            localStorage.setItem(REFERENCES_PILL_KEY, behavior);
        } catch {
            /* best-effort — the in-memory value still drives behavior this session */
        }
    }

    return {
        matterSelectionBehavior,
        referencesPillBehavior,
        setMatterSelectionBehavior,
        setReferencesPillBehavior,
    };
});
